import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve, RouterStateSnapshot } from '@angular/router';
import { BehaviorSubject, EMPTY, Observable, of, Subject, throwError, from } from 'rxjs';
import { HttpParams } from '@angular/common/http';
import { FuseUtils } from '@fuse/utils';
import { IReading, IReadingRaw, Reading } from '@shared/models/electric-meter-reading';
import { catchError, debounceTime, map, switchMap, tap } from 'rxjs/operators';
import { BcmApiService } from '@bcmApiServices/bcm-api.service';
import { FilesApiService } from '@bcmApiServices/files.api-service';
import { IFile } from '@shared/interfaces/file';
import { ElectricMeter } from '@shared/models/electric-meter';
import { ElectricMeterAssignment } from '@shared/models/electric-meter-assignment';
import { ConfirmDialogService } from '@sharedComponents/dialogs/confirm-dialog/confirm-dialog.service';
import { InformationDialogService } from '@sharedComponents/dialogs/information-dialog/information-dialog.service';
import { AppNotificationService } from '@core/services/app-notification.service';
import { TranslationService } from '@core/translation/translation.service';
import { ElectricMeterProductKey } from '@shared/models/electric-meter-product-type';
import { resolveElectricMeterId } from './readings-meter-id.util';
import {
    ElectricMeterReadingDeleteStartResult,
    ElectricMeterReadingDeletionImpact,
    ElectricMeterReadingDeletionScope,
} from './electric-meter-reading-deletion.types';

@Injectable({providedIn: 'root'})
export class ReadingsService implements Resolve<any> {
    onReadingsChanged: BehaviorSubject<Reading[]>;
    onReadingChanged: BehaviorSubject<Reading>;
    onSearchTextChanged: Subject<any>;
    onFilterChanged: Subject<any>;

    readings: Reading[];
    reading: Reading;

    searchText: string;
    filterBy: string;
    meterCabinetId: string;
    electricMeterId: string;

    private _refreshSubject = new Subject<ElectricMeter | undefined>();

    constructor(
        private _apiService: BcmApiService,
        private _filesApiService: FilesApiService,
        private _confirmDialogService: ConfirmDialogService,
        private _informationDialogService: InformationDialogService,
        private _notificationService: AppNotificationService,
    ) {
        // Set the defaults
        this.onReadingsChanged = new BehaviorSubject([]);
        this.onReadingChanged = new BehaviorSubject(null);
        this.onSearchTextChanged = new Subject();
        this.onFilterChanged = new Subject();

        // debounceTime coalesces rapid successive refresh triggers (e.g. updateReading
        // immediately followed by addReading in the Zwischenablesung flow) into a single
        // HTTP call. switchMap cancels any in-flight request if a newer one arrives.
        // catchError prevents a single failed request from killing the subscription.
        this._refreshSubject
            .pipe(
                debounceTime(300),
                switchMap(electricMeter =>
                    this.getReadings(electricMeter).pipe(catchError(() => EMPTY))
                ),
            )
            .subscribe();

        this.onSearchTextChanged.subscribe(searchText => {
            this.searchText = searchText;
            this.getReadings();
        });

        this.onFilterChanged.subscribe(filter => {
            this.filterBy = filter;
            this.getReadings();
        });
    }

    private triggerRefresh(electricMeter?: ElectricMeter): void {
        this._refreshSubject.next(electricMeter);
    }

    resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {
        this.meterCabinetId = route.params['meterCabinetId'];
        this.electricMeterId = route.params['electricMeterId'];

        return this.getReadings();
    }

    getReadings(electricMeter?: ElectricMeter): Observable<Reading[]> {
        const meterId = electricMeter?.id || this.electricMeterId;
        if (!meterId) {
            return throwError(() => new Error(TranslationService.translate('bcm.electricMeters.error.noMeterIdAvailable')));
        }
        return this._apiService.get<IReadingRaw[]>(`electric-meters/${meterId}/readings`)
            .pipe(
                map((readingsRaw: IReadingRaw[]) => {
                    let readings = readingsRaw.map(readingRaw => new Reading(readingRaw));

                    if (this.searchText && this.searchText !== '') {
                        readings = FuseUtils.filterArrayByString(readings, this.searchText);
                    }

                    this.readings = readings;
                    this.onReadingsChanged.next(this.readings);

                    return this.readings;
                })
            );
    }

    getReading(id: number, electricMeter?: ElectricMeter): Observable<any> {
        const meterId = electricMeter?.id || this.electricMeterId;
        if (!meterId) {
            return throwError(() => new Error(TranslationService.translate('bcm.electricMeters.error.noMeterIdAvailable')));
        }
        return this._apiService.get<IReadingRaw>(`electric-meters/${meterId}/readings/${id}`)
            .pipe(
                map((response: IReadingRaw) => {
                    this.reading = new Reading(response);
                    this.onReadingChanged.next(this.reading);
                    return this.reading;
                })
            );
    }

    updateReading(reading: Reading, notSavedStartImage: IFile, electricMeter?: ElectricMeter, notSavedEndImage?: IFile, skipRefresh = false): Observable<any> {
        const uploadStart$: Observable<number | null> = notSavedStartImage
            ? (reading.image?.id
                ? this._filesApiService.update(notSavedStartImage)
                : this._filesApiService.add(notSavedStartImage))
            : of(null);

        const uploadEnd$ = (startImageId: number | null): Observable<number | null> =>
            notSavedEndImage
                ? (reading.endImage?.id
                    ? this._filesApiService.update(notSavedEndImage)
                    : this._filesApiService.add(notSavedEndImage))
                : of(null);

        return uploadStart$.pipe(
            switchMap(imageId =>
                uploadEnd$(imageId).pipe(
                    switchMap(endImageId => {
                        const body: any = {...reading};
                        if (imageId != null) { body.imageId = imageId; }
                        if (endImageId != null) { body.endImageId = endImageId; }
                        return this._apiService
                            .put<IReading>(
                                `electric-meters/${electricMeter?.id || this.electricMeterId}/readings/${reading.id}`,
                                body
                            )
                            .pipe(
                                tap(() => { if (!skipRefresh) { this.triggerRefresh(electricMeter); } }),
                                map((response: any) => {
                                    this.reading = new Reading(response);
                                    this.onReadingChanged.next(this.reading);
                                    return this.reading;
                                }),
                            );
                    })
                )
            )
        );
    }

    addReading(reading: Partial<Reading>, notSavedStartImage: IFile, electricMeter?: ElectricMeter, notSavedEndImage?: IFile): Observable<any> {
        const uploadStart$: Observable<number | null> = notSavedStartImage
            ? this._filesApiService.add(notSavedStartImage)
            : of(null);

        const uploadEnd$ = (): Observable<number | null> =>
            notSavedEndImage
                ? this._filesApiService.add(notSavedEndImage)
                : of(null);

        return uploadStart$.pipe(
            switchMap(imageId =>
                uploadEnd$().pipe(
                    switchMap(endImageId => {
                        const body: any = {...reading};
                        if (imageId != null) { body.imageId = imageId; }
                        if (endImageId != null) { body.endImageId = endImageId; }
                        return this._apiService
                            .post<IReading>(
                                `electric-meters/${electricMeter?.id || this.electricMeterId}/readings`,
                                body
                            )
                            .pipe(
                                tap(() => this.triggerRefresh(electricMeter)),
                                map((response: any) => {
                                    this.reading = new Reading(response);
                                    this.onReadingChanged.next(this.reading);
                                    return this.reading;
                                }),
                            );
                    })
                )
            )
        );
    }

    updateStartReading(
        reading: Reading,
        body: {
            startDate: Date;
            startMeterReading: number;
            reason?: string;
            note?: string;
            imageId?: number | null;
            productType?: { id: number } | null;
            product?: { id: number } | null;
            quantity?: number | null;
        },
        notSavedStartImage?: IFile,
        electricMeter?: ElectricMeter,
    ): Observable<Reading> {
        const uploadStart$: Observable<number | null> = notSavedStartImage
            ? (reading.image?.id
                ? this._filesApiService.update(notSavedStartImage)
                : this._filesApiService.add(notSavedStartImage))
            : of(null);

        return uploadStart$.pipe(
            switchMap(imageId => {
                const payload: any = {...body};
                if (imageId != null) { payload.imageId = imageId; }
                return this._apiService
                    .put<IReading>(
                        `electric-meters/${electricMeter?.id || reading.electricMeter?.id || this.electricMeterId}/readings/${reading.id}/start`,
                        payload
                    )
                    .pipe(
                        tap(() => this.triggerRefresh(electricMeter)),
                        map((response: any) => {
                            this.reading = new Reading(response);
                            this.onReadingChanged.next(this.reading);
                            return this.reading;
                        }),
                    );
            })
        );
    }

    createReadingPosition(reading: IReading, body: {
        boatId?: number,
        personId?: number,
        companyId?: number
    }, electricMeter?: ElectricMeter): Observable<any> {
        const meterId = resolveElectricMeterId(reading, electricMeter, this.electricMeterId);
        if (meterId == null) {
            return throwError(() => new Error(TranslationService.translate('bcm.electricMeters.error.noMeterIdAvailable')));
        }
        return this._apiService
            .post<any>(
                `electric-meters/${meterId}/readings/${reading.id}/positions`,
                body
            ).pipe(tap(() => this.triggerRefresh(electricMeter)));
    }


    updateReadingPosition(reading: Reading): Observable<any> {
        return this._apiService
            .put<any>(
                `electric-meters/${reading.bcm_electric_meters_id}/readings/${reading.id}/positions/${reading.invoicePosition.id}`,
                reading
            );
    }

    handlePostSaveInvoicePosition(
        reading: Reading,
        onAssignPosition: (reading: Reading) => void,
        onPositionUpdated?: () => void
    ): void {
        if (reading.invoicePosition) {
            const {invoicePosition} = reading;
            if (invoicePosition.bcm_invoices_id == null && invoicePosition.bcm_receipts_id == null) {
                this._confirmDialogService
                    .setTitle(TranslationService.translate('bcm.electricMeters.dialog.attentionTitle'))
                    .setBody(TranslationService.translate('bcm.electricMeters.dialog.openPositionBodyHtml'))
                    .openWithCallback(() => {
                        this.updateReadingPosition(reading)
                            .subscribe(() => {
                                this._notificationService.showSuccess(TranslationService.translate('bcm.electricMeters.notify.positionChanged'));
                            })
                            .add(() => onPositionUpdated?.());
                    });
            } else {
                this._informationDialogService
                    .setTitle(TranslationService.translate('bcm.electricMeters.dialog.informationTitle'))
                    .setBody(TranslationService.translate('bcm.electricMeters.dialog.consumptionAlreadyBilledBodyHtml'))
                    .open();
            }
        } else if (reading.endDate && reading.endMeterReading != null && reading.productType?.key !== ElectricMeterProductKey.NO_CHARGE) {
            onAssignPosition(reading);
        }
    }

    getDeletionImpact(
        readingId: number,
        electricMeter?: ElectricMeter,
        scope: ElectricMeterReadingDeletionScope = 'full',
    ): Observable<ElectricMeterReadingDeletionImpact> {
        const meterId = electricMeter?.id || this.electricMeterId;
        if (!meterId) {
            return throwError(() => new Error(TranslationService.translate('bcm.electricMeters.error.noMeterIdAvailable')));
        }
        const params = new HttpParams().set('scope', scope);
        return this._apiService.get<ElectricMeterReadingDeletionImpact>(
            `electric-meters/${meterId}/readings/${readingId}/deletion-impact`,
            params,
        );
    }

    deleteReadingWithOptions(
        reading: IReading,
        scope: ElectricMeterReadingDeletionScope,
        deleteOpenInvoicePositions: boolean,
        electricMeter?: ElectricMeter,
    ): Observable<unknown> {
        const meterId = resolveElectricMeterId(reading, electricMeter, this.electricMeterId);
        if (meterId == null) {
            return throwError(() => new Error(TranslationService.translate('bcm.electricMeters.error.noMeterIdAvailable')));
        }
        const params = new HttpParams().set(
            'deleteOpenInvoicePositions',
            deleteOpenInvoicePositions ? 'true' : 'false',
        );
        const base = `electric-meters/${meterId}/readings/${reading.id}`;
        let request$: Observable<unknown>;
        if (scope === 'start') {
            request$ = this._apiService.delete<ElectricMeterReadingDeleteStartResult>(`${base}/start`, params);
        } else if (scope === 'end') {
            request$ = this._apiService.delete<IReading>(`${base}/end`, params);
        } else {
            request$ = this._apiService.delete(`${base}`, params);
        }
        return request$.pipe(tap(() => this.triggerRefresh(electricMeter)));
    }

    confirmAndDeleteReading(
        reading: IReading,
        scope: ElectricMeterReadingDeletionScope,
        electricMeter?: ElectricMeter,
    ): Observable<ElectricMeterReadingDeleteStartResult | unknown> {
        return this.getDeletionImpact(reading.id, electricMeter, scope).pipe(
            switchMap(impact => {
                if (impact.hasInvoicedPosition) {
                    this._informationDialogService
                        .setTitle(TranslationService.translate('bcmElectricMeters.readings.deleteBlockedInvoicedTitle'))
                        .setBody(TranslationService.translate('bcmElectricMeters.readings.deleteBlockedInvoicedBody'))
                        .open();
                    return EMPTY;
                }

                return from(this.confirmDeletion(scope, impact, reading)).pipe(
                    switchMap(confirmed => {
                        if (!confirmed) {
                            return EMPTY;
                        }
                        return this.deleteReadingWithOptions(
                            reading,
                            scope,
                            impact.hasOpenInvoicePosition,
                            electricMeter,
                        );
                    }),
                );
            }),
        );
    }

    private confirmDeletion(
        scope: ElectricMeterReadingDeletionScope,
        impact: ElectricMeterReadingDeletionImpact,
        reading?: IReading,
    ): Promise<boolean> {
        const isIntermediateEnd = scope === 'end' && reading?.reason === 'Zwischenablesung';

        const titleKey = scope === 'start'
            ? 'bcmElectricMeters.readings.deleteStartTitle'
            : scope === 'end'
                ? (isIntermediateEnd
                    ? 'bcmElectricMeters.readings.deleteIntermediateEndMenu'
                    : 'bcmElectricMeters.readings.deleteEndTitle')
                : impact.electricMeterBoatAssignmentId != null
                    ? 'bcmElectricMeters.readings.removeWithAssignmentTitle'
                    : 'bcmElectricMeters.readings.removeTitle';

        let bodyKey = scope === 'start'
            ? 'bcmElectricMeters.readings.deleteStartBody'
            : scope === 'end'
                ? (isIntermediateEnd
                    ? 'bcmElectricMeters.readings.deleteIntermediateEndBody'
                    : 'bcmElectricMeters.readings.deleteEndBody')
                : impact.electricMeterBoatAssignmentId != null
                    ? 'bcmElectricMeters.readings.removeWithAssignmentBody'
                    : 'bcmElectricMeters.readings.removeBody';

        if (impact.hasOpenInvoicePosition) {
            bodyKey = 'bcmElectricMeters.readings.deleteWithOpenInvoicePositionsBody';
        }

        return new Promise(resolve => {
            this._confirmDialogService
                .setDialogConfig({ disableClose: false })
                .setTitle(TranslationService.translate(titleKey))
                .setBody(TranslationService.translate(bodyKey))
                .setTheme('warn')
                .openWithCallback(
                    () => resolve(true),
                    () => resolve(false),
                );
        });
    }

    /** @deprecated Use confirmAndDeleteReading(..., 'full', ...) */
    deleteReading(reading, electricMeter?: ElectricMeter): Observable<any> {
        return this.confirmAndDeleteReading(reading, 'full', electricMeter);
    }

    disconnectElectricMeterBoat(reading: Reading, assignment: ElectricMeterAssignment): Observable<any> {
        return this._apiService.put(`electric-meters/${reading.electricMeter.id}/boats/${assignment.boat.id}/disconnect`, {...assignment})
            .pipe(tap(() => this.triggerRefresh()));
    }

}
